import { useEffect } from 'react'

import { getDynamicHealthTool } from '@hhgtech/hhg-components'
import { NavMenuContentType } from '@hhgtech/hhg-components/components/navigationV2/index'
import { GetServerSidePropsResult } from 'next'
import { shallow } from 'zustand/shallow'

import { cleanUnicodeUrl } from 'api/utils/paths'
import { verifyNotFoundToRedirect } from 'api/utils/redirects'
import { checkMobile } from 'api/utils/validators'
import { getServerSideProps as getBabyGrowthServerSideProps } from 'components/pages/healthTools/babyGrowth/getServerSideProps'
import type { BabyGrowthPageProps } from 'components/pages/healthTools/babyGrowth/index'
import type { Props as BabyPoopPageProps } from 'components/pages/healthTools/babyPoop'
import { getServerSideProps as getBabyPoopServerSideProps } from 'components/pages/healthTools/babyPoop/getServerSideProps'
import { getServerSideProps as getBabyVaccinationServerSideProps } from 'components/pages/healthTools/babyVaccine/getServerSideProps'
import type { BabyVaccinationPageProps } from 'components/pages/healthTools/babyVaccine/index'
import type { Props as BMIPageProps } from 'components/pages/healthTools/bmi'
import { getServerSideProps as getBMIServerSideProps } from 'components/pages/healthTools/bmi/getServerSideProps'
import type { Props as BMRPageProps } from 'components/pages/healthTools/bmr'
import { getServerSideProps as getBMRServerSideProps } from 'components/pages/healthTools/bmr/getServerSideProps'
import type { Props as DueDatePageProps } from 'components/pages/healthTools/due-date'
import { getServerSideProps as getDueDateServerSideProps } from 'components/pages/healthTools/due-date/getServerSideProps'
import {
  BMIPageComponent,
  BMRPageComponent,
  BabyGrowthPageComponent,
  BabyPoopPageComponent,
  BabyVaccinePageComponent,
  DueDatePageComponent,
  OvulationPageComponent,
  PWGPageComponent,
  THRPageComponent,
} from 'components/pages/healthTools/dynamicToolLoader'
import type { Props as OvulationPageProps } from 'components/pages/healthTools/ovulation'
import { getServerSideProps as getOvulationServerSideProps } from 'components/pages/healthTools/ovulation/getServerSideProps'
import type { Props as PWGPageProps } from 'components/pages/healthTools/pwg'
import { getServerSideProps as getPWGServerSideProps } from 'components/pages/healthTools/pwg/getServerSideProps'
import { getServerSideProps as getTHRServerSideProps } from 'components/pages/healthTools/thrGetServerSideProps'
import type { Props as THRPageProps } from 'components/pages/healthTools/thrPage'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'
import type { ExtendedGetServerSideProps, HealthTool } from 'interfaces/types'
import { useHealthToolStore } from 'state/healthTool'

export type BaseProps = {
  isMobileSsr?: boolean
  pageType?: string
  resolvedUrl?: string
  healthToolTitle?: string
  toolName?: string
  siteName?: string
  currentCategory?: string
  dataNavMenuContent?: NavMenuContentType
}
export type Props =
  | DueDatePageProps
  | BMIPageProps
  | BMRPageProps
  | OvulationPageProps
  | PWGPageProps
  | THRPageProps
  | BabyVaccinationPageProps
  | BabyGrowthPageProps
  | BabyPoopPageProps

const HealthToolsPage = (props: Props) => {
  const { pageType } = props
  const { setCurrentHealthToolType } = useHealthToolStore(
    (state) => state,
    shallow,
  )

  useEffect(() => {
    setCurrentHealthToolType(pageType as string)
  }, [pageType])
  const renderHealthToolPage = () => {
    switch (pageType) {
      case HEALTH_TOOL_TYPE.DUE_DATE: {
        return <DueDatePageComponent {...(props as DueDatePageProps)} />
      }
      case HEALTH_TOOL_TYPE.BMI: {
        return <BMIPageComponent {...(props as BMIPageProps)} />
      }
      case HEALTH_TOOL_TYPE.BMR: {
        return <BMRPageComponent {...(props as BMRPageProps)} />
      }
      case HEALTH_TOOL_TYPE.OVULATION: {
        return <OvulationPageComponent {...(props as OvulationPageProps)} />
      }
      case HEALTH_TOOL_TYPE.PWG: {
        return <PWGPageComponent {...(props as PWGPageProps)} />
      }
      case HEALTH_TOOL_TYPE.THR: {
        return <THRPageComponent {...(props as THRPageProps)} />
      }
      case HEALTH_TOOL_TYPE.BABY_VACCINE: {
        return <BabyVaccinePageComponent {...(props as any)} />
      }
      case HEALTH_TOOL_TYPE.BABY_GROWTH: {
        return <BabyGrowthPageComponent {...(props as any)} />
      }
      case HEALTH_TOOL_TYPE.BABY_POOP: {
        return <BabyPoopPageComponent {...(props as BabyPoopPageProps)} />
      }
      default:
        return null
    }
  }

  return renderHealthToolPage()
}

export const getServerSideProps: ExtendedGetServerSideProps<
  Props | BaseProps
> = async (ctx) => {
  const { query, req, locale = 'en-PH' } = ctx

  const userAgent = `${req.headers['user-agent']}`
  const isMobileSsr = checkMobile(userAgent)
  const { slug } = query
  let stringSlug = Array.isArray(slug) ? slug.join('/') : slug
  stringSlug = cleanUnicodeUrl(stringSlug || '').replace(/health-tools\//gi, '')

  let returnRes: GetServerSidePropsResult<Props | BaseProps> = {
    notFound: true,
  }

  const allTools = (await getDynamicHealthTool(locale)) as HealthTool[]
  if (!allTools?.length)
    return {
      redirect: {
        destination: '/health-tools/',
        statusCode: 307,
      },
    }

  const currentHealthTool = allTools.find((tool: HealthTool) =>
    stringSlug?.startsWith(
      cleanUnicodeUrl(tool.link)
        .replace(/health-tools\//gi, '')
        .replace(/fil\//gi, '')
        .split('/')
        .filter(Boolean)
        .join('/'),
    ),
  )

  if (stringSlug && currentHealthTool) {
    const isExactMatch =
      cleanUnicodeUrl(currentHealthTool.link)
        .replace(/health-tools\//gi, '')
        .replace(/fil\//gi, '')
        .split('/')
        .filter(Boolean)
        .join('/') ===
      cleanUnicodeUrl(Array.isArray(slug) ? slug.join('/') : slug || '')
    switch (currentHealthTool.template) {
      case HEALTH_TOOL_TYPE.DUE_DATE:
        returnRes = await getDueDateServerSideProps(ctx)
        break
      case HEALTH_TOOL_TYPE.BMI:
        returnRes = await getBMIServerSideProps(ctx)
        break
      case HEALTH_TOOL_TYPE.BMR:
        returnRes = await getBMRServerSideProps(ctx)
        break
      case HEALTH_TOOL_TYPE.OVULATION:
        returnRes = await getOvulationServerSideProps(ctx)
        break
      case HEALTH_TOOL_TYPE.PWG:
        returnRes = await getPWGServerSideProps(ctx)
        break
      case HEALTH_TOOL_TYPE.THR:
        returnRes = await getTHRServerSideProps(ctx)
        break
      case HEALTH_TOOL_TYPE.BABY_VACCINE:
        returnRes = await getBabyVaccinationServerSideProps(ctx)
        break
      case HEALTH_TOOL_TYPE.BABY_GROWTH:
        returnRes = await getBabyGrowthServerSideProps(ctx)
        break

      case HEALTH_TOOL_TYPE.BABY_POOP:
        if (!isExactMatch)
          return {
            notFound: true,
          }
        returnRes = await getBabyPoopServerSideProps(ctx)
        break
      default:
        break
    }
  }
  if ('notFound' in returnRes) {
    returnRes = verifyNotFoundToRedirect(
      slug,
      locale,
    ) as GetServerSidePropsResult<Props | (BaseProps & Props)>

    if ('notFound' in returnRes && returnRes.notFound) {
      returnRes = {
        redirect: {
          destination: '/health-tools/',
          statusCode: 301,
        },
      }
    }
  }

  if ('props' in returnRes) {
    returnRes.props = await returnRes.props
    returnRes.props.isMobileSsr = isMobileSsr
  }

  return returnRes
}

export default HealthToolsPage
